import React, { useState, Fragment, useCallback, useEffect } from 'react';
import { translate } from '../Services/translation';
import { useDispatch, useSelector } from 'react-redux';
import { reloadPayment, reloadShipment } from '../Actions/Checkout.action';
import { addClinicalPartFee, removeClinicalPartFee } from '../Actions/Checkout.action';
import { update, loadError, setClinicalPartActive } from '../Actions/Cart.action';
import { debounce, forEach } from 'lodash-es';
import { catchError } from '../Actions/Error.action';
import CheckoutDiscountCodes from './Checkout.DiscountCodes';
import {removeDecimals,formatVat } from  '../Utils/formatfunctions.js';
import { SUBSCRIPTION_INTERVAL_MONTHLY } from '../constants';
const mapCartToAbort = {};

const quantityDispatch = debounce((articleNumber, quantity, dispatch) => {
    mapCartToAbort[articleNumber] = new AbortController();
    dispatch(update(articleNumber, quantity, mapCartToAbort[articleNumber]))
        .then(() => {
            dispatch(reloadShipment());
            dispatch(reloadPayment());
        })
        .catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
}, 500);

const Cart = (props) => {
    const dispatch = useDispatch();
    const [removingRow, setRemovingRow] = useState({});
    const cart = useSelector((state) => state.cart);
    const [orderRows, setOrderRows] = useState(cart?.orderRows);
    const { discountRows } = cart;
    const [quantityUpdatedArticlenumber, setquantityUpdatedArticlenumber] = useState("");  
    const [showConfirmPopUp, setshowConfirmPopUp] = useState(false);

    useEffect((() => {
        if (cart.showCheckoutOnlyCardPopUp) {
            setshowConfirmPopUp(true);
        }
        if (cart.lensDurationCategoryExistingInCart === SUBSCRIPTION_INTERVAL_MONTHLY && !cart.nonSubscriptionProductExists) {
            if (cart.orderRows.some(x => x.isSixMonthsDeliverySubscription) && cart.orderRows.some(x => !x.isSixMonthsDeliverySubscription)) {
                let notSixMonths = cart.orderRows.filter(x => !x.isSixMonthsDeliverySubscription);
                let sixMonths = cart.orderRows.filter(x => x.isSixMonthsDeliverySubscription);
                let maxPackageSize = 0;
                sixMonths.forEach((orderRow) => {
                    if (orderRow.packageSize > maxPackageSize) {
                        maxPackageSize = orderRow.packageSize;
                    }
                })
                notSixMonths.forEach((orderRow) => {
                    updateQuantity(orderRow.articleNumber, orderRow.additionalInfo[0].identifier, orderRow.additionalInfo[0].rightStrength !== undefined && 
                        orderRow.additionalInfo[0].rightStrength !== "", maxPackageSize / orderRow.packageSize);
                    setquantityUpdatedArticlenumber(orderRow.articleNumber);
                })
            }  
        }

        if (!cart.nonSubscriptionProductExists) {            
            if (cart.clinicalPartActive) {
                addClinicalFee();
            } else {
                removeClinicalFee();
            }         
        }
        else {
            removeClinicalFee();
        }   
    }), []);

    useEffect(() => {
        setOrderRows(cart?.orderRows);
        if (!cart.nonSubscriptionProductExists && 
            !cart.orderRows.some(x => x.isSixMonthsDeliverySubscription)) {
            setquantityUpdatedArticlenumber("");
        }
    }, [cart]);

    const removeOrderRow = useCallback(
        (articleNumber, identifier, isRight, quantity) =>
            dispatch(update(articleNumber, quantity, identifier, isRight, true)).then(() => {
                dispatch(reloadShipment());
                dispatch(reloadPayment());
            }),
        [dispatch]
    );

    const updateQuantity = useCallback(
        (articleNumber, identifier, isRight, quantity) =>
            dispatch(update(articleNumber, quantity, identifier, isRight)).then(() =>
                dispatch(reloadPayment())
            ),
        [dispatch]
    );

    const handleQuantityInput = useCallback(
        (articleNumber, quantity, rowSystemId, ensureCorrectness = true) => {
            // In case of onBlur event, we need ensureCorrectness as true in order to ensure if the value is a valid float number
            // In case of onChange event, we accept invalid float number, but we don't send any request to the server.
            let floatQuantity = parseFloat(quantity);
            let validValue = !isNaN(floatQuantity) && floatQuantity > 0;
            if (ensureCorrectness) {
                quantity = validValue ? floatQuantity : 1;
                floatQuantity = quantity;
                validValue = true;
            }
            const index = orderRows.findIndex(
                (item) => item.rowSystemId === rowSystemId
            );
            const oldQuantity = parseFloat(orderRows[index].quantity);
            if (floatQuantity !== oldQuantity) {
                mapCartToAbort[articleNumber] &&
                    mapCartToAbort[articleNumber].abort();
                quantityDispatch && quantityDispatch.cancel();
                validValue &&
                    quantityDispatch(articleNumber, quantity, dispatch);
            }
            const tempOrderRows = [...orderRows];
            tempOrderRows[index] = {
                ...tempOrderRows[index],
                quantity,
            };
            setOrderRows(tempOrderRows);
        },
        [dispatch, orderRows]
    );

    const getListOfDates = (cycleDuration, freeMonthCanBeGiven) => {
        let listOfDates = [];
        let date = new Date();
        for(let i = 1; i < cycleDuration; i++) {
            date.setDate(date.getDate() + 30);
            let year = date.getFullYear();
            let month = (date.getMonth() + 1).toString().padStart(2, '0');
            let day = date.getDate().toString().padStart(2, '0');
            listOfDates.push(i === cycleDuration - 1 && freeMonthCanBeGiven ? translate("coptikk.subscription.freemonth") : `${day}.${month}.${year}`);
        }
        return listOfDates;
    }

    const handleClinicalFeeCheckbox = (event) => {        
        dispatch(setClinicalPartActive(event.target.checked));

        if (event.target.checked) {
            addClinicalFee();
        }
        else {
            removeClinicalFee();
        }
    }    

    const addClinicalFee = useCallback(() => 
        dispatch(addClinicalPartFee()).then(() => 
            dispatch(reloadPayment())
        ), 
        [dispatch]
    );

    const removeClinicalFee = useCallback(() => 
        dispatch(removeClinicalPartFee()).then(() => 
            dispatch(reloadPayment())
        ), 
        [dispatch]
    );

    const ProductImage = useCallback(({ order }) => {
        return (
            <img
                className="checkout-cart__image"
                src={order.image}
                alt={order.name}
            />
        );
    }, []);

    const ProductName = useCallback(({ order }) => {
        return (
            <Fragment>
                <a href={order.url}>{order.name}</a>
                <span className="checkout-cart__brand-name">{order.brand}</span>
            </Fragment>
        );
    }, []);

    const ProductPrice = useCallback(({ order }) => {
        return (
            <Fragment>
                {order.isFreeGift ? order.campaignPrice : order.price}
            </Fragment>
        );
    }, []);

    const ProductQuantity = useCallback(
        (order) => {
            return (
                <Fragment>
                    {order.isFreeGift ? (
                        <div>{order.quantity}</div>
                    ) : (
                        <input
                            className="checkout-cart__input"
                            type="number"
                            min="1"
                            maxLength={3}
                            value={order.quantity.toString()}
                            onChange={(event) =>
                                handleQuantityInput(
                                    order.articleNumber,
                                    event.target.value,
                                    order.rowSystemId,
                                    false
                                )
                            }
                            onBlur={(event) =>
                                handleQuantityInput(
                                    order.articleNumber,
                                    event.target.value,
                                    order.rowSystemId
                                )
                            }
                        />
                    )}
                </Fragment>
            );
        },
        [handleQuantityInput]
    );

    const ProductTotalPrice = ({ order }) => {
        return (
            <Fragment>
                {order.isFreeGift ? order.totalCampaignPrice : order.totalPrice}
            </Fragment>
        );
    };

    const RemoveBtn = useCallback(
        ({ order, identifier, isRight, quantity }) => {
            return (
                <Fragment>
                    {!order.isFreeGift && !removingRow[order.rowSystemId + identifier + isRight] && (
                        <a
                            className="table__icon table__icon--delete"
                            onClick={() =>
                                setRemovingRow({
                                    ...removingRow,
                                    [order.rowSystemId + identifier + isRight]: true,
                                })
                            }
                            title={translate('general.remove')}
                        ></a>
                    )}
                    {!order.isFreeGift && removingRow[order.rowSystemId + identifier + isRight] && (
                        <Fragment>
                            <a
                                className="table__icon table__icon--accept"
                                onClick={() =>
                                    removeOrderRow(order.articleNumber, identifier, isRight, quantity)
                                }
                                title={translate('general.ok')}
                            ></a>
                            <a
                                className="table__icon table__icon--cancel"
                                onClick={() =>
                                    setRemovingRow({
                                        ...removingRow,
                                        [order.rowSystemId + identifier + isRight]: false,
                                    })
                                }
                                title={translate('general.cancel')}
                            ></a>
                        </Fragment>
                    )}
                </Fragment>
            );
        },
        [removeOrderRow, removingRow]
    );

    const CartTotal = useCallback(({ cart }) => {
        const isCampaign = parseInt(cart.deliveryCost, 10) === parseInt(cart.discount.replace(/[^\d]/g, ''), 10);
        let subscriptionDates = getListOfDates(cart?.subscriptionPayments?.cycleDuration, cart?.subscriptionPayments?.freeMonthCanBeGiven);
        let monthlyFreightPrice = parseFloat(cart.deliveryCost) / cart?.subscriptionPayments?.cycleDuration;
        return (
            <div className='columns checkout__summary-rows'>
                {cart.nonSubscriptionProductExists ? (
                    <>
                        <div className='total-row'>
                        <p className='total'>{translate('coptikk.checkout.cart.orderrow.total')}</p>
                        <p className='total'>{removeDecimals(cart.orderTotal)}</p>
                        </div>
                        <div className='total-row'>
                            <p>{translate('coptikk.checkout.cart.discount')}{cart.discountValue && (<span>({cart.discountValue})</span>)}</p>
                            <p>{removeDecimals(cart.discount)}</p>
                        </div>
                        <div className='total-row'>
                            <p>{translate('coptikk.checkout.cart.delivery.cost')}</p>
                            {(cart.isFreeDeliveryCampaignExists && parseInt(cart.deliveryCost, 10) > 0) ? (
                                <p>0,-</p>) : (<p>{removeDecimals(cart.deliveryCost)}</p>)}

                        </div>
                        <div className='grand-total-row'>
                            <div className='description'>
                                <p className='grand-total'>{translate('coptikk.checkout.price.total')}</p>
                                <p className='vat'>{translate('coptikk.checkout.price.inc.vat')}</p>
                            </div>
                            <p className='grand-total'>{removeDecimals(cart.grandTotal)}</p>
                        </div>
                        <div className='total-row'>
                            <p>{translate('coptikk.checkout.cart.vat')}</p>
                            <p>{formatVat(cart.vat)}</p>
                        </div>
                    </>
                    ) : (
                    <>
                        <div className='total-row'>
                        <p className='total'>{translate('coptikk.checkout.cart.orderrow.total')}</p>
                        <p className='total'>{removeDecimals(cart.subscriptionPayments.monthlyCartTotal.toString())}</p>
                        </div>
                        <div className='total-row'>
                            <p>{translate('coptikk.checkout.cart.discount')}{cart.discountValue && (<span>({cart.discountValue})</span>)}</p>
                            <p>{removeDecimals(cart.discount)}</p>
                        </div>
                        <div className='total-row'>
                            <p>{translate('coptikk.checkout.cart.delivery.cost')}</p>
                            {(cart.isFreeDeliveryCampaignExists && parseInt(cart.deliveryCost, 10) > 0) ? (
                                <p>0,-</p>) : (<p>{removeDecimals(cart.subscriptionPayments.monthlyDeliveryCost.toString())}</p>)}

                        </div>
                        <div className='grand-total-row'>
                            <div className='description'>
                                <p className='grand-total'>{translate('coptikk.checkout.price.total')}</p>
                                <p className='vat'>{translate('coptikk.checkout.price.inc.vat')}</p>
                            </div>
                            <p className='grand-total'>{removeDecimals(cart.grandTotal)}</p>
                        </div>
                        <div className='total-row'>
                            <p>{translate('coptikk.checkout.cart.vat')}</p>
                            <p>{formatVat(cart.vat)}</p>
                        </div>
                        <p className='planned-payments'>{translate('coptikk.checkout.cart.plannedpayments')}</p>
                        {subscriptionDates.map((date, index) => (
                            <div className='total-row planned' key={date}>
                                <p>{date}</p>
                                <p>{cart?.subscriptionPayments.freeMonthCanBeGiven && index === subscriptionDates.length - 1 ? 
                                "0,-" : removeDecimals(cart.grandTotal.toString())}</p>
                            </div>
                        ))}
                    </>
                )}
                
            </div>
        );
    }, []);

    return (
        <div className="row checkout__container">
            <div className="small-12 simple-table">
                <div className="row small-unstack no-margin checkout-cart__header-row">
                    <div className="columns small-12 medium-6 large-6 checkout-cart__header-item"></div>
                    <div className="columns small-12 medium-3 large-3 checkout-cart__header-item quantity">
                        {translate('checkout.cart.header.quantity')}
                    </div>
                    <div className="columns small-12 medium-2 large-2 checkout-cart__header-item">
                        {translate('checkout.cart.header.total')}
                    </div>
                    <div className="columns small-12 medium-1 large-1 checkout-cart__header-item"></div>
                </div>
                {orderRows.map((order) => {
                    return (order.additionalInfo && order.additionalInfo.map((additionalInfoItem) => {
                        return <> {
                            additionalInfoItem.rightStrength &&
                            <div className="row small-unstack no-margin checkout-cart__row" key={order.rowSystemId + additionalInfoItem.identifier + true}>
                                <div className="columns small-12 medium-6 large-6 checkout-cart__image-container">
                                    <div className="checkout-cart__image-wrapper">
                                        <ProductImage order={order} />
                                    </div>
                                    <div className="checkout-cart__image-info">
                                        <ProductName order={order} />
                                        <div className='checkout-cart__product-category'>{order.categoryName}</div>
                                        <div className='checkout-cart__eye-row'>
                                            <div>{translate('coptikk.product.righteye')}</div>
                                            <div>
                                                {additionalInfoItem.rightStrength && <span className='my-recommendations__spec'>{additionalInfoItem.rightStrength}</span>}
                                                {additionalInfoItem.rightCurve &&
                                                    <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.curve.short')}: {additionalInfoItem.rightCurve}</span>}
                                                {additionalInfoItem.rightDiameter &&
                                                    <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.diameter.short')}: {additionalInfoItem.rightDiameter}</span>}
                                                {additionalInfoItem.rightCylinder &&
                                                    <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.cylinder.short')}: {additionalInfoItem.rightCylinder}</span>}
                                                {additionalInfoItem.rightAxis &&
                                                    <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.axis')}: {additionalInfoItem.rightAxis}</span>}
                                                {additionalInfoItem.rightAdd &&
                                                    <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.addfield')}: {additionalInfoItem.rightAdd}</span>}
                                                {additionalInfoItem.rightColor &&
                                                    <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.color')}: {additionalInfoItem.rightColor}</span>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="columns small-12 medium-3 large-3 checkout-cart__package-size">
                                    <select value={additionalInfoItem.rightPacket}
                                        onChange={(e) => updateQuantity(order.articleNumber, additionalInfoItem.identifier, true, e.target.value)}>
                                        
                                        {quantityUpdatedArticlenumber !== order.articleNumber ? order.packetSizeList.map(x => {
                                            return <option value={x}>{x} pk a {order.unitPrice},-</option>
                                        }) : (
                                            <option hidden value={additionalInfoItem.rightPacket}>{additionalInfoItem.rightPacket} pk a {order.unitPrice},-</option>
                                        )}
                                    </select>
                                </div>
                                {cart.nonSubscriptionProductExists ? (
                                    <div className="checkout-cart__total-price columns small-6 medium-2 large-2 simple-table__cell--no-break-word">
                                        {parseInt(additionalInfoItem.rightPacket) * order.unitPrice},-
                                    </div>
                                ) : (
                                    <div className="checkout-cart__total-price columns small-6 medium-2 large-2 simple-table__cell--no-break-word">
                                        {removeDecimals(((parseInt(additionalInfoItem.rightPacket) * order.unitPrice) / cart.subscriptionPayments.cycleDuration).toString())} / {translate('coptikk.checkout.cart.month')} 
                                    </div>
                                )}
                                <div className="columns small-6 medium-1 large-1 checkout-cart__delete-column">
                                    <RemoveBtn order={order} identifier={additionalInfoItem.identifier} isRight={true}
                                        quantity={order.quantity - parseInt(additionalInfoItem.rightPacket)} />
                                </div>
                                {quantityUpdatedArticlenumber === order.articleNumber && (
                                    <p>{translate('coptikk.checkout.cart.extrapackage')}</p>
                                )}
                            </div>}
                            {additionalInfoItem.leftStrength &&
                                <div className="row small-unstack no-margin checkout-cart__row" key={order.rowSystemId + additionalInfoItem.identifier + false}>
                                    <div className="columns small-12 medium-6 large-6 checkout-cart__image-container">
                                        <div className="checkout-cart__image-wrapper">
                                            <ProductImage order={order} />
                                        </div>
                                        <div className="checkout-cart__image-info">
                                            <ProductName order={order} />
                                            <div className='checkout-cart__product-category'>{order.categoryName}</div>
                                            <div className='checkout-cart__eye-row'>
                                                <div>{translate('coptikk.product.lefteye')}</div>
                                                <div>
                                                    {additionalInfoItem.leftStrength && <span className='my-recommendations__spec'>{additionalInfoItem.leftStrength}</span>}
                                                    {additionalInfoItem.leftCurve &&
                                                        <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.curve.short')}: {additionalInfoItem.leftCurve}</span>}
                                                    {additionalInfoItem.leftDiameter &&
                                                        <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.diameter.short')}: {additionalInfoItem.leftDiameter}</span>}
                                                    {additionalInfoItem.leftCylinder &&
                                                        <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.cylinder.short')}: {additionalInfoItem.leftCylinder}</span>}
                                                    {additionalInfoItem.leftAxis &&
                                                        <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.axis')}: {additionalInfoItem.leftAxis}</span>}
                                                    {additionalInfoItem.leftAdd &&
                                                        <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.addfield')}: {additionalInfoItem.leftAdd}</span>}
                                                    {additionalInfoItem.leftColor &&
                                                        <span className='my-recommendations__spec'>{' '}{translate('coptikk.product.color')}: {additionalInfoItem.leftColor}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="columns small-12 medium-3 large-3 checkout-cart__package-size">
                                        <select value={additionalInfoItem.leftPacket}
                                            onChange={(e) => updateQuantity(order.articleNumber, additionalInfoItem.identifier, false, e.target.value)}>
                                            
                                            {quantityUpdatedArticlenumber !== order.articleNumber ? order.packetSizeList.map(x => {
                                                return <option value={x}>{x} pk a {order.unitPrice},-</option>
                                            }) : (
                                                <option hidden value={additionalInfoItem.leftPacket}>{additionalInfoItem.leftPacket} pk a {order.unitPrice},-</option>
                                            )}
                                        </select>
                                    </div>
                                    {cart.nonSubscriptionProductExists ? (
                                        <div className="checkout-cart__total-price columns small-6 medium-2 large-2 simple-table__cell--no-break-word">
                                            {parseInt(additionalInfoItem.leftPacket) * order.unitPrice},-
                                        </div>
                                    ) : (
                                        <div className="checkout-cart__total-price columns small-6 medium-2 large-2 simple-table__cell--no-break-word">
                                            {removeDecimals(((parseInt(additionalInfoItem.leftPacket) * order.unitPrice) / cart.subscriptionPayments.cycleDuration).toString())} / {translate('coptikk.checkout.cart.month')} 
                                        </div>
                                    )}
                                    <div className="columns small-6 medium-1 large-1 checkout-cart__delete-column">
                                        <RemoveBtn order={order} identifier={additionalInfoItem.identifier} isRight={false}
                                            quantity={order.quantity - parseInt(additionalInfoItem.leftPacket)} />
                                    </div>
                                    {quantityUpdatedArticlenumber === order.articleNumber && (
                                    <p>{translate('coptikk.checkout.cart.extrapackage')}</p>
                                    )}
                                </div>}
                        </>
                    }))
                }
                )
                }
                {!cart.nonSubscriptionProductExists && 
                    <div className="row small-unstack no-margin checkout-cart__clinical-add">
                        <div className='clinical-info large-9'>
                            <div className='clinical-checkbox'>
                                <input 
                                    id="clinicalcheckbox"
                                    type="checkbox"
                                    checked={cart.clinicalPartActive}
                                    onChange={(event) => handleClinicalFeeCheckbox(event)}
                                />
                                <label for="clinicalcheckbox">{translate('coptikk.general.clinicalexamination')}</label>
                            </div>
                            <p className='clinical-description'>{translate('coptikk.general.clinicalexamination.description')}</p>
                        </div>
                        <div className='clinical-price large-3'>{removeDecimals(cart.clinicalPartPrice.toString())} / {translate('coptikk.checkout.cart.month')}</div>
                    </div>
                }
                {discountRows.map((order) => (
                    <div
                        className="row small-unstack no-margin checkout-cart__row"
                        key={order.rowSystemId}
                    >
                        <div className="columns small-12 medium-4 large-5 checkout-cart__image-container">
                            <div className="checkout-cart__discount-info checkout-cart__discount-row">
                                <ProductName order={order} />
                            </div>
                        </div>
                        <div className="columns small-3 medium-2 large-2 simple-table__cell--no-break-word"></div>
                        <div className="columns small-2 medium-2 large-2"></div>
                        <div className="checkout-cart__discount-price columns small-2 medium-3 large-2 simple-table__cell--no-break-word">
                            <ProductTotalPrice order={order} />
                        </div>
                        <div className="columns small-3 medium-1 large-1"></div>
                    </div>
                ))}
                <div className="small-unstack no-margin checkout-cart__row checkout__cart-summary">
                    <div className='columns checkout__discount-container'>
                        <CheckoutDiscountCodes />
                            {(!cart.sameSupplier || (cart.subscriptionExistsForRightEye || cart.subscriptionExistsForLeftEye)) && 
                            <div className='columns checkout__information-box'>
                                <div className='icon'>i</div>
                                <div className='text'>
                                    {!cart.sameSupplier && <p>{translate('coptikk.subscription.checkout.differentsupplier')}</p>}
                                    {(cart.subscriptionExistsForRightEye || cart.subscriptionExistsForLeftEye) && <p>{translate('coptikk.subscription.checkout.termstext')}
                                    <a className='checkout__information-link' href={cart.subscriptionInformationPage}>
                                        {translate("coptikk.subscription.checkout.informationpage")}</a></p>}
                                </div>
                            </div> }                       
                    </div>
                    <CartTotal cart={cart} />
                </div>
            </div>
            {props.authenticated && !cart.nonSubscriptionProductExists && showConfirmPopUp && 
                <div className='subscription-pop-up'>
                <div className='subscription-pop-up__content'>
                    <h2 className='subscription-pop-up__title'>{translate("coptikk.subscription.checkout.onlycardpopup.header")}</h2>
                    <p className='subscription-pop-up__description'>{translate("coptikk.subscription.checkout.onlycardpopup.description")}</p>                    
                    <a className='subscription-pop-up__pause-button' onClick={() =>setshowConfirmPopUp(false)}>
                        {translate("coptikk.subscription.checkout.onlycardpopup.buttontext")}</a>
                </div>
            </div>
        }   
        </div>
    );
};

export default Cart;
